<i18n src="@/i18n/components/create-event.json"></i18n>

<template>
  <div class="create-event">
    <section v-if="step === 1" class="hero is-medium is-warning c-background--image">
      <div class="hero-body">
        <div class="container has-text-centered">
          <h1 class="title is-2 c-heading__title">{{ $t('stepOne.title') }}</h1>
          <h2 class="subtitle is-4 c-heading__subtitle">{{ $t('stepOne.subtitle') }}</h2>

          <div class="columns is-mobile">
            <div class="column is-half-tablet is-offset-one-quarter-tablet">
              <div class="field is-grouped is-grouped-centered is-grouped-multiline">
                <p class="control is-expanded">
                  <input v-model="eventName" class="input is-medium" :placeholder="$t('stepOne.eventName')" @keyup.enter="nextStep()">
                </p>
                <p class="control">
                  <button @click="nextStep()" class="button is-medium is-primary" disabled>
                    {{ $t('stepOne.create') }}
                  </button>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section v-if="step === 2" class="hero is-warning c-background--image">
      <div class="hero-body">
        <div class="container has-text-centered">
          <h2 class="subtitle is-5 c-heading__subtitle">{{ $t('stepTwo.step', {current: 2, total: 2}) }}</h2>
          <h1 class="title is-3 c-heading__title">{{ $t('stepTwo.userNameLabel') }}</h1>

          <div class="columns is-mobile">
            <div class="column is-half-tablet is-offset-one-quarter-tablet">

              <div class="card">
                <div class="card-content">
                  <div class="content">
                    <div class="field">
                      <p class="control has-icons-left">
                        <input v-model="initiator.name" class="input" :placeholder="$t('stepTwo.name')" required @keyup.enter="finish()">
                        <span class="icon is-small is-left"><i class="fa fa-user-circle"></i></span>
                      </p>
                    </div>

                    <div class="field has-addons">
                      <p class="control is-expanded has-icons-left">
                        <input
                          v-model="initiator.email"
                          name="email"
                          class="input"
                          data-vv-as="'Email'"
                          :class="{'is-danger': errors.has('email') }"
                          v-validate="'email'"
                          type="text"
                          :placeholder="'Email (' + $t('optional') + ')'"
                        >
                        <span class="icon is-small is-left"><i class="fa fa-envelope"></i></span>
                        <span v-show="errors.has('email')" class="help is-danger">{{ errors.first('email') }}</span>
                      </p>
                      <div class="control">
                        <b-tooltip
                          :label="$t('stepTwo.emailInfo')"
                          position="is-left"
                          type="is-dark"
                          multilined
                        >
                          <a class="button">
                            <span class="icon is-right has-text-info"><i class="fa fa-info-circle"></i></span>
                          </a>
                        </b-tooltip>
                      </div>
                    </div>
                  </div>
                </div>
                <footer class="card-footer">
                  <a @click="getBack()" class="card-footer-item">{{ $t('stepTwo.previous') }}</a>
                  <div class="card-footer-item">
                    <button @click="finish()" class="button is-medium is-primary" :class="{ 'is-loading': isLoading }" :disabled="initiator.name.trim() === '' || isLoading">{{ $t('stepTwo.finish') }}</button>
                  </div>
                </footer>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
  import gql from 'graphql-tag'
  import { bus } from '@/shared/bus'
  import Email from 'smtpjs'

  const uuidv4 = require('uuid/v4')

  const createEventAndUser = gql`
    mutation createEvent($eventName: String!, $initiatorName: String!, $initiatorEmail: String, $secret: String!) {
      createEvent(name: $eventName) {
        id
        name
      }
      createUser(name: $initiatorName, email: $initiatorEmail, secret: $secret) {
        id
        name
        email
      }
    }
  `

  const addToUserInitiatedEvents = gql`
  mutation addToUserInitiatedEvents($eventId: ID!, $userId: ID!) {
    addToUserInitiatedEvents(initiatedEventsEventId: $eventId, initiatorUserId: $userId) {
      initiatedEventsEvent {
        id
        name
      }
      initiatorUser {
        id
        name
      }
    }
    addToEventParticipants(attendedEventsEventId: $eventId, participantsUserId: $userId) {
      attendedEventsEvent {
        id
        name
      }
      participantsUser {
        id
        name
      }
    }
  }
  `

  export default {
    name: 'create-event',
    data () {
      return {
        step: 1,
        eventName: '',
        initiator: {
          name: '',
          email: '',
          secret: uuidv4()
        },
        isLoading: false
      }
    },
    methods: {
      sendMail (eventId, adminId) {
        if (this.initiator.email) {
          Email.send(
            `Freatle <freatle@pulsar.surge.sh>`,
            this.initiator.email,
            this.$t('emailSubject', {eventName: this.eventName}),
            this.$t('emailBody', {eventId: eventId, eventName: this.eventName, adminId: adminId}),
            'smtp.sendgrid.net',
            'apikey',
            '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'
          )
        }
      },
      nextStep () {
        this.step++

        this.$ga && this.$ga.event('Event', 'click', 'nextStep')
      },
      finish () {
        this.$validator.validateAll().then((result) => {
          if (result) {
            this.$ga && this.$ga.event('Event', 'click', 'create')

            this.createEvent()
          } else {
            this.$toast.open({
              message: this.$t('fixErrors'),
              position: 'is-bottom',
              type: 'is-danger'
            })

            this.$ga && this.$ga.exception('Event:click:create')
          }
        })
      },
      getBack () {
        this.step--
      },
      createEvent () {
        this.isLoading = true

        // Mutation
        this.$apollo.mutate({
          mutation: createEventAndUser,
          variables: {
            eventName: this.eventName,
            initiatorName: this.initiator.name,
            initiatorEmail: this.initiator.email,
            secret: this.initiator.secret
          }
        }).then((data) => {
          let eventId = data.data.createEvent.id

          // Bind the created event to the created user
          this.$apollo.mutate({
            mutation: addToUserInitiatedEvents,
            variables: {
              eventId: data.data.createEvent.id,
              userId: data.data.createUser.id
            }
          }).then((data) => {
            this.$toast.open({
              message: this.$t('stepOne.eventCreated'),
              position: 'is-bottom',
              type: 'is-success'
            })

            localStorage.setItem('userId', data.data.addToUserInitiatedEvents.initiatorUser.id)

            this.sendMail(eventId, data.data.addToUserInitiatedEvents.initiatorUser.id)

            // Redirect the user to the event page
            this.$router.push({name: 'Event', params: {id: data.data.addToUserInitiatedEvents.initiatedEventsEvent.id}})
          }).catch((error) => {
            bus.$emit('toast:new', {type: 'danger'})

            this.$ga && this.$ga.exception(error.message || error)
          }).then(() => {
            this.isLoading = false
          })
        }).catch((error) => {
          bus.$emit('toast:new', {type: 'danger'})

          this.$ga && this.$ga.exception(error.message || error)

          this.isLoading = false
        })
      }
    }
  }
</script>



// WEBPACK FOOTER //
// src/components/CreateEvent.vue